import { z } from 'zod';
import { ADMIN_PERMISSIONS, AdminPermission, OtpChannel, OtpPurpose, UserRole, UserStatus, VerificationStatus, } from './enums.js';
import { AVAILABILITY_DAYS, AVAILABILITY_TIME_SLOTS, OTP_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, US_STATES, WORK_FREQUENCIES, WORK_TYPES, } from './constants.js';
/* -------------------------------------------------------------------------- */
/* Health                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Contract for `GET /api/health`.
 *
 * The api validates its own response against this before sending, and both
 * frontends parse the response with it — a single source of truth proving the
 * shared package is wired into all three workspaces.
 */
export const healthResponseSchema = z.object({
    status: z.literal('ok'),
    db: z.enum(['connected', 'disconnected']),
});
/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */
export const emailSchema = z.email().trim().toLowerCase().max(150);
/** E.164, e.g. +919812345678. Kept strict so OTP delivery is unambiguous. */
export const phoneSchema = z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/, 'Phone must be in E.164 format, e.g. +919812345678')
    .max(20);
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, 'Password must be at least ' + PASSWORD_MIN_LENGTH + ' characters')
    .max(PASSWORD_MAX_LENGTH);
export const otpCodeSchema = z
    .string()
    .trim()
    .regex(new RegExp('^\\d{' + OTP_LENGTH + '}$'), 'Code must be ' + OTP_LENGTH + ' digits');
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be formatted YYYY-MM-DD');
/** Either an email or a phone number — the two ways to identify an account. */
export const identifierSchema = z.union([emailSchema, phoneSchema]);
export const otpChannelSchema = z.enum([OtpChannel.EMAIL, OtpChannel.SMS]);
const personNameSchema = z.string().trim().min(1).max(100);
/* Address primitives — the profile tables are US-shaped: free-text city, a
 * two-letter state code, a 5- or 9-digit ZIP, and a country that defaults to
 * the United States in the database. */
export const citySchema = z.string().trim().min(1).max(100);
export const stateSchema = z.enum(US_STATES.map((entry) => entry.code));
export const zipCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, 'Enter a 5-digit ZIP code, e.g. 94105')
    .max(20);
export const countrySchema = z.string().trim().min(1).max(100);
/** Shape written to `teens.availability`. Every part is optional. */
export const availabilitySchema = z.object({
    days: z.array(z.enum(AVAILABILITY_DAYS)).max(AVAILABILITY_DAYS.length).optional(),
    timeSlots: z
        .array(z.enum(AVAILABILITY_TIME_SLOTS))
        .max(AVAILABILITY_TIME_SLOTS.length)
        .optional(),
    notes: z.string().trim().max(200).optional(),
});
/** Shape written to `teens.work_preferences`. */
export const workPreferencesSchema = z.object({
    type: z.array(z.enum(WORK_TYPES)).max(WORK_TYPES.length).optional(),
    frequency: z.enum(WORK_FREQUENCIES).optional(),
    maxDistanceMiles: z.number().int().min(1).max(100).optional(),
});
/** The address block every self-registering role may fill in. */
const addressFields = {
    city: citySchema.optional(),
    state: stateSchema.optional(),
    zipCode: zipCodeSchema.optional(),
    country: countrySchema.optional(),
};
/* -------------------------------------------------------------------------- */
/* M01 — Registration [FR-M01.1, FR-M01.2]                                    */
/* -------------------------------------------------------------------------- */
/**
 * Both email and phone are collected because `users.email` and `users.phone`
 * are NOT NULL UNIQUE in the database design. FR-M01.1's "email or mobile" is
 * honoured by letting the registrant choose which one receives the OTP.
 * See DECISIONS.md D-009.
 */
const registerBase = z.object({
    email: emailSchema,
    phone: phoneSchema,
    password: passwordSchema,
    /** Which channel the signup OTP is delivered to. */
    otpChannel: otpChannelSchema.default(OtpChannel.EMAIL),
});
export const registerTeenSchema = registerBase.extend({
    role: z.literal(UserRole.TEEN),
    firstName: personNameSchema,
    lastName: personNameSchema,
    dateOfBirth: isoDateSchema,
    parentEmail: emailSchema.optional(),
    /** Optional profile fields, fillable at signup or later during onboarding. */
    bio: z.string().trim().max(300).optional(),
    ...addressFields,
    photoUrl: z.string().trim().max(255).optional(),
    availability: availabilitySchema.optional(),
    resumeUrl: z.string().trim().max(255).optional(),
    workPreferences: workPreferencesSchema.optional(),
    /** `skills.public_id` values, joined onto the teen through `teen_skills`. */
    skillIds: z.array(z.uuid()).max(20).optional(),
});
export const registerParentSchema = registerBase.extend({
    role: z.literal(UserRole.PARENT),
    firstName: personNameSchema,
    lastName: personNameSchema,
    /** Optional profile fields, fillable at signup or later during onboarding. */
    ...addressFields,
    photoUrl: z.string().trim().max(255).optional(),
});
export const registerJobPosterSchema = registerBase.extend({
    role: z.literal(UserRole.JOB_POSTER),
    firstName: personNameSchema,
    lastName: personNameSchema,
    /** e.g. Freelance Tutor, Independent Contractor. */
    professionalTitle: z.string().trim().max(150).optional(),
    /** Optional profile fields, fillable at signup or later during onboarding. */
    bio: z.string().trim().max(300).optional(),
    /** Short display label shown beside their jobs, e.g. "Austin, TX". */
    location: z.string().trim().max(255).optional(),
    streetAddress: z.string().trim().max(255).optional(),
    ...addressFields,
    photoUrl: z.string().trim().max(255).optional(),
});
export const registerInputSchema = z.discriminatedUnion('role', [
    registerTeenSchema,
    registerParentSchema,
    registerJobPosterSchema,
]);
/* -------------------------------------------------------------------------- */
/* Skills catalogue — `GET /api/skills`                                        */
/* -------------------------------------------------------------------------- */
/** One row of the `skills` table, as the api exposes it. */
export const skillSchema = z.object({
    /** UUID `public_id` — this is what `skillIds` on signup refers to. */
    publicId: z.string(),
    name: z.string(),
    /** Optional grouping used to section the picker, e.g. "Household". */
    category: z.string().nullable(),
});
export const skillsResponseSchema = z.object({
    skills: z.array(skillSchema),
});
/* -------------------------------------------------------------------------- */
/* M01 — OTP verification [FR-M01.1]                                          */
/* -------------------------------------------------------------------------- */
/** Purposes a client may drive directly. LOGIN is issued by the server only. */
const clientOtpPurposeSchema = z.enum([OtpPurpose.SIGNUP, OtpPurpose.PASSWORD_RESET]);
export const verifyOtpInputSchema = z.object({
    identifier: identifierSchema,
    code: otpCodeSchema,
    purpose: clientOtpPurposeSchema.default(OtpPurpose.SIGNUP),
});
export const resendOtpInputSchema = z.object({
    identifier: identifierSchema,
    purpose: clientOtpPurposeSchema.default(OtpPurpose.SIGNUP),
});
/* -------------------------------------------------------------------------- */
/* M01 — Login & sessions [FR-M01.3, FR-M01.6]                                */
/* -------------------------------------------------------------------------- */
export const loginInputSchema = z.object({
    identifier: identifierSchema,
    password: z.string().min(1, 'Password is required'),
    /** Free-text device label, stored on the session for device tracking. */
    deviceInfo: z.string().trim().max(255).optional(),
});
/** Step-up MFA exchange for privileged roles. [FR-M01.3] */
export const mfaChallengeInputSchema = z.object({
    mfaToken: z.string().min(1),
    code: otpCodeSchema,
    deviceInfo: z.string().trim().max(255).optional(),
});
export const refreshInputSchema = z.object({
    refreshToken: z.string().min(1),
});
/* -------------------------------------------------------------------------- */
/* M01 — Password reset [FR-M01.8]                                            */
/* -------------------------------------------------------------------------- */
export const forgotPasswordInputSchema = z.object({
    identifier: identifierSchema,
});
export const resetPasswordInputSchema = z.object({
    identifier: identifierSchema,
    code: otpCodeSchema,
    password: passwordSchema,
});
/* -------------------------------------------------------------------------- */
/* M01 — Change password                                                      */
/* -------------------------------------------------------------------------- */
export const changePasswordInputSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
});
/* -------------------------------------------------------------------------- */
/* M01 — Admin user management [FR-M01.5]                                     */
/* -------------------------------------------------------------------------- */
export const adminPermissionSchema = z.enum([
    AdminPermission.MODERATION,
    AdminPermission.COMPLIANCE,
    AdminPermission.FINANCE,
    AdminPermission.SUPPORT,
    AdminPermission.SUPER_ADMIN,
]);
const permissionListSchema = z.array(adminPermissionSchema).max(ADMIN_PERMISSIONS.length);
export const createAdminInputSchema = z.object({
    email: emailSchema,
    phone: phoneSchema,
    password: passwordSchema,
    /** Named role the admin is attached to, e.g. "Finance Admin". */
    roleName: z.string().trim().min(2).max(50),
    department: z.string().trim().max(100).optional(),
    permissions: permissionListSchema,
});
export const updateAdminPermissionsInputSchema = z.object({
    permissions: permissionListSchema,
});
/* -------------------------------------------------------------------------- */
/* M01 — Responses                                                            */
/* -------------------------------------------------------------------------- */
export const userRoleSchema = z.enum([
    UserRole.TEEN,
    UserRole.PARENT,
    UserRole.JOB_POSTER,
    UserRole.ADMIN,
]);
export const userStatusSchema = z.enum([
    UserStatus.PENDING,
    UserStatus.ACTIVE,
    UserStatus.SUSPENDED,
    UserStatus.BANNED,
]);
export const verificationStatusSchema = z.enum([
    VerificationStatus.PENDING,
    VerificationStatus.APPROVED,
    VerificationStatus.REJECTED,
]);
/**
 * The authenticated principal. `id` is the UUID `public_id` — sequential
 * database ids are never exposed over the API.
 */
export const authUserSchema = z.object({
    id: z.string(),
    role: userRoleSchema,
    status: userStatusSchema,
    email: z.string(),
    phone: z.string(),
    displayName: z.string(),
    emailVerified: z.boolean(),
    phoneVerified: z.boolean(),
    /** ADMIN only; empty for everyone else. */
    permissions: z.array(adminPermissionSchema),
    /** TEEN only. True while a minor's guardian link is unverified. [FR-M01.2] */
    awaitingParentApproval: z.boolean(),
    /** TEEN only. */
    isMinor: z.boolean().nullable(),
});
export const authTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Access-token lifetime in seconds. */
    expiresIn: z.number().int().positive(),
});
export const authSessionResponseSchema = z.object({
    user: authUserSchema,
    tokens: authTokensSchema,
});
/** Login either completes, or issues an MFA challenge for privileged roles. */
export const loginResponseSchema = z.discriminatedUnion('mfaRequired', [
    z.object({
        mfaRequired: z.literal(false),
        user: authUserSchema,
        tokens: authTokensSchema,
    }),
    z.object({
        mfaRequired: z.literal(true),
        mfaToken: z.string(),
        channel: otpChannelSchema,
        sentTo: z.string(),
        /** Present only outside production, so the flow is testable without an inbox. */
        devCode: z.string().optional(),
    }),
]);
export const registerResponseSchema = z.object({
    userId: z.string(),
    status: userStatusSchema,
    channel: otpChannelSchema,
    sentTo: z.string(),
    awaitingParentApproval: z.boolean(),
    /** Present only outside production, so the flow is testable without an inbox. */
    devCode: z.string().optional(),
});
export const otpDispatchResponseSchema = z.object({
    channel: otpChannelSchema,
    sentTo: z.string(),
    devCode: z.string().optional(),
});
export const sessionSummarySchema = z.object({
    id: z.string(),
    deviceInfo: z.string().nullable(),
    ipAddress: z.string().nullable(),
    issuedAt: z.string(),
    expiresAt: z.string(),
    /** True for the session the calling refresh token belongs to. */
    current: z.boolean(),
});
export const adminSummarySchema = z.object({
    id: z.string(),
    email: z.string(),
    phone: z.string(),
    status: userStatusSchema,
    roleName: z.string(),
    department: z.string().nullable(),
    permissions: z.array(adminPermissionSchema),
    createdAt: z.string(),
    lastLoginAt: z.string().nullable(),
});
/* -------------------------------------------------------------------------- */
/* M03 — Profile [FR-M03.1]                                                    */
/* -------------------------------------------------------------------------- */
/**
 * An empty string clears a nullable column; omitting the key leaves it alone.
 * Without this an editor could set a value but never take it back.
 */
const clearable = (schema) => z.union([schema, z.literal('')]).optional();
/**
 * Every field is optional: a PATCH carries only what changed. The address
 * fields reuse the same primitives registration validates against, so a value
 * that was accepted at signup stays acceptable here.
 *
 * `firstName`, `lastName` and `country` are NOT NULL in the database, so they
 * are settable but not clearable.
 */
export const updateProfileInputSchema = z.object({
    firstName: personNameSchema.optional(),
    lastName: personNameSchema.optional(),
    bio: clearable(z.string().trim().max(300)),
    photoUrl: clearable(z.string().trim().max(255)),
    city: clearable(citySchema),
    state: clearable(stateSchema),
    zipCode: clearable(zipCodeSchema),
    country: countrySchema.optional(),
    professionalTitle: clearable(z.string().trim().max(150)), // JobPoster only
    /** JobPoster only. Setting a name is what makes the account an organization. */
    organizationName: clearable(z.string().trim().max(150)),
    organizationWebsite: clearable(z.string().trim().max(255)),
    location: clearable(z.string().trim().max(255)), // JobPoster only
    streetAddress: clearable(z.string().trim().max(255)), // JobPoster only
    availability: availabilitySchema.optional(), // Teen only
    workPreferences: workPreferencesSchema.optional(), // Teen only
    resumeUrl: clearable(z.string().trim().max(255)), // Teen only
    /** Teen only. Replaces the whole `teen_skills` set when present. */
    skillIds: z.array(z.uuid()).max(20).optional(),
});
/**
 * The full profile behind the signed-in account, flattened across the three
 * role tables. Fields a role does not own come back `null`, so one shape
 * serves teens, parents and job posters.
 */
export const profileDetailSchema = z.object({
    role: userRoleSchema,
    firstName: z.string(),
    lastName: z.string(),
    photoUrl: z.string().nullable(),
    bio: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zipCode: z.string().nullable(),
    country: z.string().nullable(),
    /** Where the account sits in the verification queue. */
    verificationStatus: verificationStatusSchema,
    /** ISO timestamp the profile row was created. */
    createdAt: z.string(),
    /* Teen only ------------------------------------------------------------- */
    dateOfBirth: z.string().nullable(),
    isMinor: z.boolean().nullable(),
    availability: availabilitySchema.nullable(),
    workPreferences: workPreferencesSchema.nullable(),
    resumeUrl: z.string().nullable(),
    backgroundCheckStatus: z.string().nullable(),
    skills: z.array(skillSchema),
    /** True once at least one guardian link is verified. [FR-M01.2] */
    guardianApproved: z.boolean().nullable(),
    /** The guardian address the teen named at signup, so they know who to chase. */
    invitedParentEmail: z.string().nullable(),
    /* Job poster only ------------------------------------------------------- */
    professionalTitle: z.string().nullable(),
    location: z.string().nullable(),
    streetAddress: z.string().nullable(),
    /** Null for an individual professional. [Job Poster step 2] */
    organizationName: z.string().nullable(),
    organizationWebsite: z.string().nullable(),
});
/** Envelope returned by both `GET /api/profile` and `PATCH /api/profile`. */
export const profileResponseSchema = z.object({
    user: authUserSchema,
    profile: profileDetailSchema,
});
/* -------------------------------------------------------------------------- */
/* M02 — Guardian approval [FR-M01.2]                                          */
/* -------------------------------------------------------------------------- */
/** One unverified parent–teen link, as the parent's dashboard sees it. */
export const guardianRequestSchema = z.object({
    relationshipId: z.string(),
    teenPublicId: z.string(),
    teenName: z.string(),
    /** mother | father | guardian. */
    relationshipType: z.string(),
    /** ISO timestamp the teen named this guardian. */
    requestedAt: z.string(),
});
export const guardianRequestsResponseSchema = z.object({
    requests: z.array(guardianRequestSchema),
});
//# sourceMappingURL=schemas.js.map